<template>
  <div>
    <v-dialog v-model="dialog" max-width="560px" :persistent="loadingSave">
      <v-card>
        <v-card-title class="headline">
          Marcar como sospechoso
          <v-spacer />
          <v-btn icon @click="closeModal" :disabled="loadingSave">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div v-if="loadingLookup" class="d-flex justify-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <template v-else>
            <p class="mb-4">
              <template v-if="isSuspicious">
                <strong>{{ entityLabel }}</strong> está marcado como sospechoso.
              </template>
              <template v-else>
                ¿Desea marcar a <strong>{{ entityLabel }}</strong> como sospechoso?
              </template>
            </p>

            <v-textarea
              v-model="reason"
              label="Motivo"
              outlined
              rows="4"
              auto-grow
              counter="500"
              maxlength="500"
              no-resize
              :disabled="loadingSave"
            />

            <v-alert v-if="errorMessage" type="error" dense text class="mb-0">
              {{ errorMessage }}
            </v-alert>
          </template>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn text @click="closeModal" :disabled="loadingSave">
            Cancelar
          </v-btn>
          <v-btn
            v-if="isSuspicious && canFlag"
            color="error"
            text
            :loading="loadingSave"
            :disabled="loadingLookup"
            @click="clearSuspicion"
          >
            Quitar sospecha
          </v-btn>
          <v-btn
            v-if="canFlag"
            color="primary"
            dark
            :loading="loadingSave"
            :disabled="loadingLookup"
            @click="save"
          >
            {{ isSuspicious ? 'Actualizar motivo' : 'Marcar como sospechoso' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SuspiciousFlagsService from '../services/suspicious-flags.service';

const ERROR_MESSAGES_BY_HTTP_CODE = {
  409: 'Esta entidad ya tiene una sospecha activa. Actualizá la página e intentá de nuevo.',
  404: 'No se encontró ningún registro de sospecha para actualizar.'
};

function messageForResult(result) {
  return ERROR_MESSAGES_BY_HTTP_CODE[result.httpCode] || 'Ocurrió un error. Intentá de nuevo.';
}

export default {
  name: 'SuspiciousFlagModal',
  props: {
    value: { type: Boolean, default: false },
    rut: { type: String, default: null },
    entityType: { type: String, required: true },
    entityLabel: { type: String, default: 'esta entidad' },
    // Whether the caller may save: each screen checks its own flag tag (2026-11-03).
    canFlag: { type: Boolean, default: false }
  },
  data() {
    return {
      loadingLookup: false,
      loadingSave: false,
      currentFlag: null,
      reason: '',
      errorMessage: null
    };
  },
  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); }
    },
    isSuspicious() {
      return Boolean(this.currentFlag);
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.loadStatus();
      } else {
        this.resetState();
      }
    }
  },
  methods: {
    resetState() {
      this.currentFlag = null;
      this.reason = '';
      this.errorMessage = null;
    },
    closeModal() {
      this.dialog = false;
      this.resetState();
    },
    async loadStatus() {
      this.loadingLookup = true;
      this.errorMessage = null;
      const result = await SuspiciousFlagsService.lookup(this.rut, this.entityType);
      if (result.status === 'ok') {
        this.currentFlag = result.data.suspiciousFlag;
        this.reason = this.currentFlag?.reason || '';
      } else {
        this.errorMessage = messageForResult(result);
      }
      this.loadingLookup = false;
    },
    async save() {
      if (!this.reason?.trim()) {
        this.errorMessage = 'Debe ingresar un motivo.';
        return;
      }

      this.loadingSave = true;
      this.errorMessage = null;
      const result = this.isSuspicious
        ? await SuspiciousFlagsService.update(this.rut, this.entityType, true, this.reason.trim())
        : await SuspiciousFlagsService.create(this.rut, this.entityType, this.reason.trim());

      if (result.status === 'ok') {
        this.$emit('saved');
        this.closeModal();
      } else {
        this.errorMessage = messageForResult(result);
      }
      this.loadingSave = false;
    },
    async clearSuspicion() {
      this.loadingSave = true;
      this.errorMessage = null;
      const result = await SuspiciousFlagsService.update(this.rut, this.entityType, false);

      if (result.status === 'ok') {
        this.$emit('saved');
        this.closeModal();
      } else {
        this.errorMessage = messageForResult(result);
      }
      this.loadingSave = false;
    }
  }
};
</script>
